// src/components/sprint/ManifestoHero.tsx
// (This is the new file you provided, with corrections)

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button, TickerCarousel } from '@dopomogai/ui';
import { ImpactGridVisual } from '../../visuals/ImpactGridVisual';

interface HeroData {
  title: string;
  subheadline: string;
  cta1: string;
  cta2: string;
  tickerVerbKeys: string[];
  tickerNounKeys: string[];
  tickerTimelineKeys: string[];
}

interface ManifestoHeroProps {
  heroData: HeroData;
  onAction: (actionType: string, payload?: any) => void;
}

const TranslatedTicker: React.FC<{
  itemKeys: string[];
  itemHeightRem: number;
  intervalSeconds: number;
}> = ({ itemKeys, ...props }) => {
  const { t } = useTranslation('sprint-manifesto');
  const items = useMemo(() => itemKeys.map(key => t(key)), [itemKeys, t]);
  return (
    <TickerCarousel
      items={items}
      {...props}
      activeColor="text-emerald-400"
      inactiveColor="text-slate-500"
    />
  );
};

export const ManifestoHero: React.FC<ManifestoHeroProps> = ({ heroData, onAction }) => {
  return (
    <section className="relative bg-slate-900 text-white min-h-[90vh] flex items-center py-24 md:py-32 overflow-hidden">
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 1.5, ease: 'easeInOut' } }}
      >
        <ImpactGridVisual />
      </motion.div>

      <div className="container mx-auto px-4 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }}
          className="flex flex-col items-center"
        >
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight drop-shadow-lg">
            <div className="h-20 flex justify-center">
              <TranslatedTicker itemKeys={heroData.tickerVerbKeys} itemHeightRem={5} intervalSeconds={2.0} />
            </div>
            <div className="h-20 flex justify-center">
              <TranslatedTicker itemKeys={heroData.tickerNounKeys} itemHeightRem={5} intervalSeconds={2.2} />
            </div>
            <div className="h-20 flex justify-center">
              <TranslatedTicker itemKeys={heroData.tickerTimelineKeys} itemHeightRem={5} intervalSeconds={2.4} />
            </div>
            <span className="text-white">{heroData.title}</span>
          </h1>

          <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-slate-300">
            {heroData.subheadline}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              text={heroData.cta1}
              variant="primary"
              size="lg"
              onClick={() => onAction('SHOW_MODAL', { modalType: 'BETA_WAITLIST' })}
              className="hover:shadow-[4px_4px_0_0_#10b981]"
            />
            <Button
              text={heroData.cta2}
              variant="secondary" // Changed to outline for better visual hierarchy
              size="lg"
              onClick={() => onAction('NAVIGATE_TO_PAGE', { path: '/contact?subject=AI Build Blueprint' })}
              className="hover:shadow-[4px_4px_0_0_#9ca3af]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};